import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { X, Banknote, Landmark, FileCheck, Smartphone, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useEnregistrerReglement, useReglements } from '@/hooks/useInvoices';
import { extractError } from '@/api/client';
import { formatMontant } from '@/utils/currency';
import type { Invoice } from '@/types';
import type { ModeReglement, ReglementResult } from '@/api/finance';

interface Props {
  open:       boolean;
  invoice:    Invoice | null;
  onClose:    () => void;
  onSuccess?: (resultat: ReglementResult) => void;
}

const MODES: Array<{
  valeur:      ModeReglement;
  libelle:     string;
  Icone:       typeof Banknote;
  placeholder: string;
}> = [
  { valeur: 'virement',     libelle: 'Virement',     Icone: Landmark,   placeholder: 'Référence du virement' },
  { valeur: 'cheque',       libelle: 'Chèque',       Icone: FileCheck,  placeholder: 'N° du chèque' },
  { valeur: 'especes',      libelle: 'Espèces',      Icone: Banknote,   placeholder: 'N° de reçu (facultatif)' },
  { valeur: 'mobile_money', libelle: 'Mobile Money', Icone: Smartphone, placeholder: 'N° de transaction Wave / Orange Money' },
];

const aujourdhui = () => new Date().toISOString().slice(0, 10);

/** Rouge d'erreur à 6,6:1 sur blanc — le jeton --danger (#EF4444) plafonne
 *  à 3,8:1 et ne passe pas l'AA sur du texte de 13 px. */
const ROUGE_TEXTE = '#B42318';

/** Ne garde que les chiffres : « 1 200 000 » → « 1200000 ». */
const versChiffres = (saisie: string) => saisie.replace(/[^0-9]/g, '');

/** Groupe par milliers pour la lecture : « 1200000 » → « 1 200 000 ». */
const grouper = (chiffres: string) =>
  chiffres.replace(/^0+(?=\d)/, '').replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

/**
 * Saisie d'un encaissement client, total ou partiel.
 *
 * Le restant dû est le chiffre qui décide : il est affiché en grand et
 * pré-remplit le champ, puisque le solde complet reste le cas courant. Tout ce
 * qui suit — mode, référence, date — ne fait que documenter l'encaissement.
 *
 * Le champ montant est un `type="text"` avec `inputMode="numeric"`, jamais un
 * `type="number"` : sur clavier français, ce dernier renvoie une chaîne vide
 * dès qu'un séparateur est tapé, et efface la saisie sans prévenir. Le montant
 * s'affiche groupé par milliers — sur des sommes à sept chiffres, une virgule
 * mal placée coûte un facteur dix.
 */
export const ModaleReglement = ({ open, invoice, onClose, onSuccess }: Props) => {
  const [montantTexte, setMontantTexte] = useState('');
  const [mode, setMode]                 = useState<ModeReglement>('virement');
  const [date, setDate]                 = useState(aujourdhui());
  const [reference, setReference]       = useState('');
  const [notes, setNotes]               = useState('');
  const [erreurApi, setErreurApi]       = useState<string | null>(null);
  // Délai dépassé : l'encaissement a peut-être abouti. On bloque un 2e envoi tant que la fenêtre n'est pas rouverte.
  const [aVerifier, setAVerifier]       = useState(false);

  const champMontant = useRef<HTMLInputElement>(null);
  const enregistrer  = useEnregistrerReglement();
  const { data: historique = [] } = useReglements(open && invoice ? invoice.id : undefined);

  const totalCents    = invoice ? BigInt(invoice.totalCents) : 0n;
  const dejaPayeCents = invoice ? BigInt(invoice.paidAmountCents) : 0n;
  const restantCents  = totalCents - dejaPayeCents;

  // Réinitialise à chaque ouverture — le restant dû pré-remplit le champ.
  useEffect(() => {
    if (!open || !invoice) return;
    setMontantTexte(grouper(String(restantCents / 100n)));
    setMode('virement');
    setDate(aujourdhui());
    setReference('');
    setNotes('');
    setErreurApi(null);
    setAVerifier(false);
    const t = setTimeout(() => champMontant.current?.select(), 50);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, invoice?.id]);

  useEffect(() => {
    if (!open) return;
    const surTouche = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', surTouche);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', surTouche);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  const montantCents = useMemo(() => {
    const chiffres = versChiffres(montantTexte);
    return chiffres ? BigInt(chiffres) * 100n : 0n;
  }, [montantTexte]);

  const trop     = montantCents > restantCents;
  const vide     = montantCents <= 0n;
  const valide   = !trop && !vide;
  const apres    = restantCents - montantCents;
  const soldera  = valide && apres <= 0n;
  const modeActif = MODES.find((m) => m.valeur === mode)!;

  if (!open || !invoice) return null;

  const client = invoice.contact?.companyName ?? invoice.contact?.fullName ?? 'Client';

  const soumettre = async (e: FormEvent) => {
    e.preventDefault();
    if (!valide || aVerifier) return;
    setErreurApi(null);
    try {
      const resultat = await enregistrer.mutateAsync({
        id: invoice.id,
        payload: {
          montantCents:  Number(montantCents),
          mode,
          dateReglement: date,
          reference:     reference.trim() || undefined,
          notes:         notes.trim() || undefined,
        },
      });
      onSuccess?.(resultat);
      onClose();
    } catch (err) {
      const e = extractError(err);
      if (e.code === 'DELAI_DEPASSE') {
        setAVerifier(true);
        setErreurApi('Le serveur a mis trop de temps à répondre : l’encaissement a peut-être été enregistré. Regardez l’historique ci-dessous, puis fermez et rouvrez la fenêtre avant de recommencer.');
      } else {
        setErreurApi(e.message);
      }
    }
  };

  return (
    <div
      className="ds-modal-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="ds-modal" role="dialog" aria-modal="true" aria-labelledby="titre-reglement">
        <form onSubmit={soumettre}>
          <header className="ds-modal-header">
            <div>
              <div className="ds-modal-title" id="titre-reglement">Enregistrer un encaissement</div>
              <div className="ds-modal-sub">{invoice.reference} · {client}</div>
            </div>
            <button type="button" className="ds-modal-close" onClick={onClose} aria-label="Fermer">
              <X size={18} />
            </button>
          </header>

          <div className="ds-modal-body">
            {/* ── Où en est la créance ─────────────────────────── */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                gap: 12,
                padding: 14,
                borderRadius: 10,
                background: 'var(--bg-deep)',
                border: '1px solid var(--bdr)',
                marginBottom: 18,
              }}
            >
              <Chiffre libelle="Total facture" valeur={formatMontant(totalCents)} />
              <Chiffre libelle="Déjà encaissé" valeur={formatMontant(dejaPayeCents)} />
              <Chiffre
                libelle="Restant dû"
                valeur={formatMontant(restantCents)}
                accent
              />
            </div>

            {/* ── Montant ──────────────────────────────────────── */}
            <div className="ds-fld" style={{ marginBottom: 16 }}>
              <label htmlFor="montant-reglement">Montant encaissé (FCFA)</label>
              <input
                id="montant-reglement"
                ref={champMontant}
                className="ds-fi-modal"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                value={montantTexte}
                onChange={(e) => setMontantTexte(grouper(versChiffres(e.target.value)))}
                aria-invalid={trop || undefined}
                aria-describedby={trop ? 'erreur-montant' : 'apercu-montant'}
                style={{
                  width: '100%',
                  fontSize: 20,
                  fontWeight: 600,
                  letterSpacing: '0.02em',
                  borderColor: trop ? 'var(--danger)' : undefined,
                  boxShadow:   trop ? '0 0 0 3px rgba(239,68,68,.12)' : undefined,
                }}
              />

              {trop ? (
                <div
                  id="erreur-montant"
                  role="alert"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    marginTop: 8, fontSize: 13, color: ROUGE_TEXTE,
                  }}
                >
                  <AlertTriangle size={14} style={{ flexShrink: 0 }} />
                  <span>
                    Dépasse le restant dû de {formatMontant(montantCents - restantCents)} FCFA.
                  </span>
                  <button
                    type="button"
                    onClick={() => setMontantTexte(grouper(String(restantCents / 100n)))}
                    style={{
                      marginLeft: 'auto', flexShrink: 0,
                      background: 'none', border: 'none', padding: 0,
                      color: ROUGE_TEXTE, textDecoration: 'underline',
                      fontSize: 13, cursor: 'pointer',
                    }}
                  >
                    Ramener au restant
                  </button>
                </div>
              ) : (
                <div
                  id="apercu-montant"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    marginTop: 8, fontSize: 13,
                    color: soldera ? 'var(--p)' : 'var(--tx-2)',
                  }}
                >
                  {soldera ? (
                    <>
                      <CheckCircle2 size={14} style={{ flexShrink: 0 }} />
                      <span>Ce règlement solde la facture.</span>
                    </>
                  ) : vide ? (
                    <span>Saisissez le montant reçu du client.</span>
                  ) : (
                    <span>Après ce règlement, il restera {formatMontant(apres)} FCFA à recouvrer.</span>
                  )}
                </div>
              )}
            </div>

            {/* ── Mode ─────────────────────────────────────────── */}
            <div className="ds-fld" style={{ marginBottom: 16 }}>
              <label id="libelle-mode">Mode de règlement</label>
              <div
                role="radiogroup"
                aria-labelledby="libelle-mode"
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
                  gap: 8,
                }}
              >
                {MODES.map(({ valeur, libelle, Icone }) => {
                  const actif = mode === valeur;
                  return (
                    <button
                      key={valeur}
                      type="button"
                      role="radio"
                      aria-checked={actif}
                      onClick={() => setMode(valeur)}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                        minHeight: 44, padding: '8px 10px',
                        borderRadius: 8, cursor: 'pointer', fontSize: 13,
                        fontWeight: actif ? 600 : 400,
                        border: `1px solid ${actif ? 'var(--p)' : 'var(--bdr)'}`,
                        background: actif ? 'var(--p-soft)' : 'transparent',
                        color: actif ? 'var(--p)' : 'var(--tx-2)',
                      }}
                    >
                      <Icone size={15} style={{ flexShrink: 0 }} /> {libelle}
                    </button>
                  );
                })}
              </div>
              {mode === 'especes' && (
                <div style={{ marginTop: 8, fontSize: 12, color: 'var(--tx-3)' }}>
                  Comptabilisé en caisse (571000) et non en banque.
                </div>
              )}
            </div>

            {/* ── Date + référence ─────────────────────────────── */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: 12,
                marginBottom: 16,
              }}
            >
              <div className="ds-fld">
                <label htmlFor="date-reglement">Date d'encaissement</label>
                <input
                  id="date-reglement"
                  className="ds-fi-modal"
                  type="date"
                  value={date}
                  max={aujourdhui()}
                  onChange={(e) => setDate(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
              <div className="ds-fld">
                <label htmlFor="ref-reglement">Référence</label>
                <input
                  id="ref-reglement"
                  className="ds-fi-modal"
                  type="text"
                  autoComplete="off"
                  value={reference}
                  placeholder={modeActif.placeholder}
                  onChange={(e) => setReference(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
            </div>

            <div className="ds-fld" style={{ marginBottom: 4 }}>
              <label htmlFor="notes-reglement">Note (facultatif)</label>
              <input
                id="notes-reglement"
                className="ds-fi-modal"
                type="text"
                autoComplete="off"
                value={notes}
                placeholder="Précision utile au rapprochement"
                onChange={(e) => setNotes(e.target.value)}
                style={{ width: '100%' }}
              />
            </div>

            {/* ── Encaissements antérieurs ─────────────────────── */}
            {historique.length > 0 && (
              <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--bdr)' }}>
                <div style={{ fontSize: 12, color: 'var(--tx-3)', marginBottom: 8 }}>
                  {historique.length} encaissement{historique.length > 1 ? 's' : ''} déjà enregistré{historique.length > 1 ? 's' : ''}
                </div>
                {historique.map((r) => (
                  <div
                    key={r.id}
                    style={{
                      display: 'flex', justifyContent: 'space-between', gap: 12,
                      fontSize: 13, padding: '4px 0', color: 'var(--tx-2)',
                    }}
                  >
                    <span>
                      {new Date(r.dateReglement).toLocaleDateString('fr-FR')} ·{' '}
                      {MODES.find((m) => m.valeur === r.mode)?.libelle ?? r.mode}
                      {r.reference ? ` · ${r.reference}` : ''}
                    </span>
                    <span style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                      {formatMontant(r.montantCents)} FCFA
                    </span>
                  </div>
                ))}
              </div>
            )}

            {erreurApi && (
              <div
                role="alert"
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 8,
                  marginTop: 16, padding: 12, borderRadius: 8, fontSize: 13,
                  background: 'rgba(239,68,68,.08)',
                  color: ROUGE_TEXTE,
                  border: '1px solid rgba(239,68,68,.28)',
                }}
              >
                <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
                <span>{erreurApi}</span>
              </div>
            )}
          </div>

          <div className="ds-modal-footer">
            <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>
              Annuler
            </button>
            <button
              type="submit"
              className="ds-btn ds-btn-p"
              disabled={!valide || aVerifier || enregistrer.isPending}
              style={{
                opacity:  !valide || enregistrer.isPending ? 0.5 : 1,
                cursor:   !valide || enregistrer.isPending ? 'not-allowed' : 'pointer',
                minHeight: 44,
              }}
            >
              {enregistrer.isPending
                ? 'Enregistrement…'
                : soldera
                  ? 'Encaisser et solder'
                  : 'Enregistrer l\'encaissement'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Chiffre = ({
  libelle, valeur, accent,
}: { libelle: string; valeur: string; accent?: boolean }) => (
  <div>
    <div style={{ fontSize: 11, color: 'var(--tx-3)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {libelle}
    </div>
    <div
      style={{
        fontSize: accent ? 22 : 16,
        fontWeight: accent ? 700 : 500,
        color: accent ? 'var(--p)' : 'var(--tx)',
        marginTop: 2,
        whiteSpace: 'nowrap',
      }}
    >
      {valeur} <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--tx-3)' }}>FCFA</span>
    </div>
  </div>
);
